import React, { useState, useEffect } from 'react';
import { useUtilityRates, flattenTieredStrux } from '../../hooks/useUtilityRates';
import { LoadingSpinner } from '../general/LoadingSpinner';
import TabNavigation from './TabNavigation';
import RateTable from './RateTable';
import HeatmapGrid from './HeatmapGrid';
import { FaExternalLinkAlt, FaCheck } from 'react-icons/fa';

/**
 * RateDetailPanel - Embeddable rate detail view (no routing/sidebar)
 * @param {string} rateId - Rate ID to display
 * @param {Function} onUse - Optional callback when "Use this rate" is clicked: (rateData) => void
 * @param {boolean} showUseButton - Whether to show the "Use this rate" button
 */
const pickFirstValue = (source, keys) => {
  if (!source || typeof source !== 'object') return undefined;
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) {
      return source[key];
    }
  }
  return undefined;
};

const RateDetailPanel = ({ rateId, rateData: initialRateData = null, onUse = null, showUseButton = false }) => {
  const { getRateDetail, hasAuth } = useUtilityRates();
  
  const [rateData, setRateData] = useState(initialRateData);
  const [loading, setLoading] = useState(!initialRateData && !!rateId);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');

  const tabs = [
    { id: 'basic', label: 'Basic Information' },
    { id: 'energy', label: 'Energy Rates' },
    { id: 'demand', label: 'Demand Rates' },
    { id: 'fixed', label: 'Fixed Charges' },
  ];

  useEffect(() => {
    if (initialRateData) {
      setRateData(initialRateData);
      setLoading(false);
      setError(null);
      return;
    }
    if (rateId) {
      fetchRateDetails();
      return;
    }
    setRateData(null);
    setLoading(false);
  }, [initialRateData, rateId]);

  const fetchRateDetails = async () => {
    if (!hasAuth) {
      setError('Authentication required.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getRateDetail(rateId);
      setRateData(data);
    } catch (err) {
      console.error('Error fetching rate details:', err);
      setError('Failed to load rate details.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const renderBasicInfo = () => {
    const details = rateData?.rate_details || {};
    
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <h4 className="text-base font-bold text-gray-800 mb-3 font-ui">Rate Information</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Utility Name</label>
              <p className="text-sm text-gray-900">{rateData?.utility_name || 'N/A'}</p>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">EIA ID</label>
              <p className="text-sm text-gray-900">{rateData?.eia_id || 'N/A'}</p>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Rate Name</label>
              <p className="text-sm text-gray-900">{rateData?.rate_name || 'N/A'}</p>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sector</label>
              <p className="text-sm text-gray-900">
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                  {rateData?.sector || 'N/A'}
                </span>
              </p>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Effective Date</label>
              <p className="text-sm text-gray-900">{formatDate(rateData?.effective_date)}</p>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
              <p className="text-sm text-gray-900">{formatDate(details?.endDate?.$date)}</p>
            </div>
            
            {details?.serviceType && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Service Type</label>
                <p className="text-sm text-gray-900">{details.serviceType}</p>
              </div>
            )}
            
            {details?.voltageCategory && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Voltage Category</label>
                <p className="text-sm text-gray-900">{details.voltageCategory}</p>
              </div>
            )}
          </div>
          
          {details?.description && (
            <div className="mt-4">
              <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{details.description}</p>
            </div>
          )}
          
          {details?.source && (
            <div className="mt-4">
              <label className="block text-xs font-medium text-gray-500 mb-1">Source</label>
              <a
                href={details.source}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
              >
                {details.source}
                <FaExternalLinkAlt className="text-xs" />
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderEnergyRates = () => {
    const details = rateData?.rate_details || {};
    const energyStrux = pickFirstValue(details, [
      'energyRateStrux',
      'energyratestructure',
      'energyRateStructure',
      'energy_rate_structure',
    ]);
    const energyStructure = flattenTieredStrux(energyStrux, 'energyRateTiers');
    const weekdaySchedule = pickFirstValue(details, [
      'energyWeekdaySched',
      'energyweekdayschedule',
      'energyWeekdaySchedule',
      'energyWeekdaySched',
      'energy_weekday_schedule',
    ]);
    const weekendSchedule = pickFirstValue(details, [
      'energyWeekendSched',
      'energyweekendschedule',
      'energyWeekendSchedule',
      'energy_weekend_schedule',
    ]);
    
    return (
      <div className="space-y-4">
        {energyStructure && energyStructure.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-4">
            <RateTable
              data={energyStructure}
              title="Energy Rate Structure"
              columns={[
                { key: 'period', label: 'Period', format: 'integer' },
                { key: 'tier', label: 'Tier', format: 'integer' },
                { key: 'max', label: 'Max Usage (kWh)', format: 'number' },
                { key: 'rate', label: 'Rate ($/kWh)', format: 'currency' },
                { key: 'adj', label: 'Adjustments ($/kWh)', format: 'currency' },
                { key: 'unit', label: 'Unit', format: 'string' },
              ]}
            />
          </div>
        )}
        
        {weekdaySchedule && weekdaySchedule.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-4">
            <HeatmapGrid
              schedule={weekdaySchedule}
              title="Weekday Schedule (Hour of Day)"
            />
          </div>
        )}
        
        {weekendSchedule && weekendSchedule.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-4">
            <HeatmapGrid
              schedule={weekendSchedule}
              title="Weekend Schedule (Hour of Day)"
            />
          </div>
        )}
        
        {!energyStructure && !weekdaySchedule && !weekendSchedule && (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <p className="text-gray-600 font-ui text-sm">No energy rate data available for this rate.</p>
          </div>
        )}
      </div>
    );
  };

  const renderDemandRates = () => {
    const details = rateData?.rate_details || {};
    const flatDemandStrux = pickFirstValue(details, [
      'flatDemandStrux',
      'flatdemandstructure',
      'flatDemandStructure',
      'flat_demand_structure',
    ]);
    const demandRateStrux = pickFirstValue(details, [
      'demandRateStrux',
      'demandratestructure',
      'demandstructure',
      'demandRateStructure',
      'demand_rate_structure',
    ]);
    const demandWeekdaySchedule = pickFirstValue(details, [
      'demandWeekdaySched',
      'demandweekdayschedule',
      'demandWeekdaySchedule',
      'demand_weekday_schedule',
    ]);
    const demandWeekendSchedule = pickFirstValue(details, [
      'demandWeekendSched',
      'demandweekendschedule',
      'demandWeekendSchedule',
      'demand_weekend_schedule',
    ]);
    const reactivePower = details?.demandReactPwrCharge;
    const demandKeyVals = details?.demandKeyVals;

    const flatDemandRows = flattenTieredStrux(flatDemandStrux, 'flatDemandTiers');
    const demandRateRows = flattenTieredStrux(demandRateStrux, 'demandRateTiers');
    
    return (
      <div className="space-y-4">
        {Array.isArray(demandKeyVals) && demandKeyVals.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-4">
            <RateTable
              data={demandKeyVals}
              title="Demand Notes"
              columns={[
                { key: 'key', label: 'Key', format: 'string' },
                { key: 'val', label: 'Value', format: 'string' },
              ]}
            />
          </div>
        )}

        {flatDemandRows.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-4">
            <RateTable
              data={flatDemandRows}
              title="Flat Demand Structure"
              columns={[
                { key: 'period', label: 'Period', format: 'integer' },
                { key: 'tier', label: 'Tier', format: 'integer' },
                { key: 'max', label: 'Max kW', format: 'number' },
                { key: 'rate', label: 'Rate ($/kW)', format: 'currency' },
                { key: 'adj', label: 'Adjustments ($/kW)', format: 'currency' },
              ]}
            />
          </div>
        )}
        
        {demandRateRows.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-4">
            <RateTable
              data={demandRateRows}
              title="Time-of-Use Demand Rate Structure"
              columns={[
                { key: 'period', label: 'Period', format: 'integer' },
                { key: 'tier', label: 'Tier', format: 'integer' },
                { key: 'max', label: 'Max kW', format: 'number' },
                { key: 'rate', label: 'Rate ($/kW)', format: 'currency' },
                { key: 'adj', label: 'Adjustments ($/kW)', format: 'currency' },
              ]}
            />
          </div>
        )}

        {Array.isArray(demandWeekdaySchedule) && demandWeekdaySchedule.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-4">
            <HeatmapGrid
              schedule={demandWeekdaySchedule}
              title="Demand Weekday Schedule (Hour of Day)"
            />
          </div>
        )}

        {Array.isArray(demandWeekendSchedule) && demandWeekendSchedule.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-4">
            <HeatmapGrid
              schedule={demandWeekendSchedule}
              title="Demand Weekend Schedule (Hour of Day)"
            />
          </div>
        )}
        
        {reactivePower !== undefined && reactivePower !== null && reactivePower !== '' && (
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h5 className="text-sm font-semibold text-gray-700 mb-2">Demand Reactive Power Charge</h5>
            <p className="text-xl font-bold text-gray-900">
              ${parseFloat(reactivePower).toFixed(5)} / kVAR
            </p>
          </div>
        )}
        
        {flatDemandRows.length === 0 &&
          demandRateRows.length === 0 &&
          (!Array.isArray(demandWeekdaySchedule) || demandWeekdaySchedule.length === 0) &&
          (!Array.isArray(demandWeekendSchedule) || demandWeekendSchedule.length === 0) &&
          !(Array.isArray(demandKeyVals) && demandKeyVals.length > 0) &&
          (reactivePower === undefined || reactivePower === null || reactivePower === '') && (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <p className="text-gray-600 font-ui text-sm">No demand rate data available for this rate.</p>
          </div>
        )}
      </div>
    );
  };

  const renderFixedCharges = () => {
    const details = rateData?.rate_details || {};
    const fixedChargeFirstMeter = details?.fixedChargeFirstMeter;
    const fixedChargeUnits = details?.fixedChargeUnits;
    const minCharge = details?.minCharge;
    const minChargeUnits = details?.minChargeUnits;
    
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <h4 className="text-base font-bold text-gray-800 mb-4 font-ui">Fixed Charges</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fixedChargeFirstMeter !== undefined && fixedChargeFirstMeter !== null && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <label className="block text-xs font-medium text-gray-600 mb-1">Fixed Charge (First Meter)</label>
                <p className="text-2xl font-bold text-blue-900">${parseFloat(fixedChargeFirstMeter).toFixed(2)}</p>
                <p className="text-xs text-gray-600 mt-1">{fixedChargeUnits || '$/billing period'}</p>
              </div>
            )}
            
            {minCharge !== undefined && minCharge !== null && (
              <div className="p-3 bg-green-50 rounded-lg">
                <label className="block text-xs font-medium text-gray-600 mb-1">Minimum Charge</label>
                <p className="text-2xl font-bold text-green-900">${parseFloat(minCharge).toFixed(2)}</p>
                <p className="text-xs text-gray-600 mt-1">{minChargeUnits || 'per billing period'}</p>
              </div>
            )}
          </div>
          
          {(fixedChargeFirstMeter === undefined || fixedChargeFirstMeter === null) &&
            (minCharge === undefined || minCharge === null) && (
            <div className="text-center py-6">
              <p className="text-gray-600 font-ui text-sm">No fixed charge data available for this rate.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!rateId && !rateData) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center">
          <p className="text-gray-600 font-ui">Select a rate to view details</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <LoadingSpinner message="Loading rate details..." />
      </div>
    );
  }

  if (error || !rateData) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
          <p className="text-red-600 font-ui mb-4">{error || 'Unable to load rate data.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-800 font-ui">{rateData.rate_name}</h3>
            <p className="text-sm text-gray-600 font-ui">{rateData.utility_name}</p>
          </div>
          {showUseButton && onUse && (
            <button
              onClick={() => onUse(rateData)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors font-medium text-sm"
            >
              <FaCheck />
              <span>Use this rate</span>
            </button>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex-shrink-0">
        <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto p-4">
        {activeTab === 'basic' && renderBasicInfo()}
        {activeTab === 'energy' && renderEnergyRates()}
        {activeTab === 'demand' && renderDemandRates()}
        {activeTab === 'fixed' && renderFixedCharges()}
      </div>
    </div>
  );
};

export default RateDetailPanel;
